import { useEffect, useMemo, useState } from "react";
import { AlertCircle, CalendarClock, CheckCircle2, HelpCircle, KeyRound, Layers, Plus, RefreshCw, Save, Settings2, X } from "lucide-react";
import { useNavigate } from "react-router-dom";
import AdminLayout from "./AdminLayout";
import {
  AdminApiError,
  AdminGoogleCalendarEvent,
  AdminGoogleCalendarEventPayload,
  AdminGoogleCalendarHeadquartersSetting,
  AdminStoreGoogleCalendarSetting,
  AdminUser,
  createAdminGoogleCalendarEvent,
  deleteAdminGoogleCalendarEvent,
  getAdminStoreGoogleCalendars,
  getCurrentAdmin,
  listAdminGoogleCalendarEvents,
  logout,
  startAdminHeadquartersGoogleCalendarOAuth,
  startAdminStoreGoogleCalendarOAuth,
  updateAdminGoogleCalendarEvent,
  updateAdminStoreGoogleCalendars,
} from "./api";
import { useConfirmDialog } from "./components/ConfirmDialog";
import { RangeMode, getPreferredCalendarRangeMode, setPreferredCalendarRangeMode } from "@/utils/storeCalendarDirectory";
import {
  GoogleCalendarEventForm,
  GoogleCalendarEventCalendarView,
  GoogleCalendarEventFormModal,
  defaultGoogleCalendarEventForm,
  googleCalendarEventPayloadFromForm,
  googleCalendarEventToForm,
  toggleGoogleCalendarAllDayForm,
} from "@/components/googleCalendar/GoogleCalendarEventControls";

/**
 * 門市日曆——總管理端設定與檢視頁。
 *
 * 這裡是「門市 ↔ Google 日曆」對照表唯一的維護入口：總管理處在這裡把每間門市對應的
 * Google 帳號設定好，門市端登入後會依照自己所屬的門市，自動讀到這裡設定的日曆 ID，
 * 不用門市自己再設定一次。
 *
 * 對照表由後端 StoreGoogleCalendarSetting 資料表提供，總管理端儲存後，門市端會依所屬門市讀取。
 */

const emptyHqCalendar: AdminGoogleCalendarHeadquartersSetting = {
  GoogleCalendarHeadquartersSettingId: null,
  GoogleEmail: "",
  CalendarId: "",
  CalendarName: null,
  IsEnabled: true,
  LastCheckedAt: null,
  LastErrorMessage: null,
  OAuthGoogleEmail: null,
  OAuthScope: null,
  TokenExpiresAt: null,
  AuthorizedAt: null,
  OAuthStatus: "NotConnected",
  OAuthLastErrorMessage: null,
};

type AdminCalendarViewEvent = AdminGoogleCalendarEvent & {
  SourceName?: string;
};

export default function AdminStoreCalendars() {
  const navigate = useNavigate();
  const { confirm, ConfirmDialog } = useConfirmDialog();
  const [currentUser, setCurrentUser] = useState<AdminUser | null>(null);
  const [settings, setSettings] = useState<AdminStoreGoogleCalendarSetting[]>([]);
  const [hqCalendar, setHqCalendar] = useState<AdminGoogleCalendarHeadquartersSetting>(emptyHqCalendar);
  const [viewMode, setViewMode] = useState<"hq" | "merged" | "settings">("hq");
  const [rangeMode, setRangeMode] = useState<RangeMode>(() => getPreferredCalendarRangeMode("admin"));
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [authorizingLocationId, setAuthorizingLocationId] = useState<string | null>(null);
  const [authorizingHeadquarters, setAuthorizingHeadquarters] = useState(false);
  const [events, setEvents] = useState<AdminGoogleCalendarEvent[]>([]);
  const [mergedEvents, setMergedEvents] = useState<AdminCalendarViewEvent[]>([]);
  const [eventsLoading, setEventsLoading] = useState(false);
  const [mergedEventsLoading, setMergedEventsLoading] = useState(false);
  const [eventSaving, setEventSaving] = useState(false);
  const [eventForm, setEventForm] = useState<GoogleCalendarEventForm | null>(null);
  const [calendarViewDate, setCalendarViewDate] = useState(() => new Date());
  const [showFieldHelp, setShowFieldHelp] = useState(false);
  const [dirty, setDirty] = useState(false);
  const [message, setMessage] = useState("");

  async function load() {
    setLoading(true);
    setMessage("");
    try {
      const me = await getCurrentAdmin();
      setCurrentUser(me.User);
      const data = await getAdminStoreGoogleCalendars();
      setHqCalendar(data.HqCalendar || emptyHqCalendar);
      setSettings(data.Settings || []);
      setDirty(false);
      if ((data.HqCalendar?.OAuthStatus || "") === "Connected") {
        await loadHeadquartersEvents();
      }
    } catch (error) {
      setMessage(error instanceof Error ? error.message : "Google 行事曆設定載入失敗。");
      if (error instanceof AdminApiError && error.status === 401) navigate("/admin-login", { replace: true });
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (hqCalendar.OAuthStatus === "Connected") {
      loadHeadquartersEvents();
    }
    if (viewMode === "merged") {
      loadMergedEvents();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [calendarViewDate.getFullYear(), calendarViewDate.getMonth(), viewMode]);

  async function handleLogout() {
    await logout().catch(() => undefined);
    navigate("/admin-login", { replace: true });
  }

  function updateSetting(locationId: string, patch: Partial<AdminStoreGoogleCalendarSetting>) {
    setSettings((current) => current.map((item) => (item.LocationId === locationId ? { ...item, ...patch } : item)));
    setDirty(true);
  }

  function updateHqCalendar(patch: Partial<AdminGoogleCalendarHeadquartersSetting>) {
    setHqCalendar((current) => ({ ...current, ...patch }));
    setDirty(true);
  }

  function updateRangeMode(mode: RangeMode) {
    setRangeMode(mode);
    setPreferredCalendarRangeMode("admin", mode);
  }

  async function saveSettings() {
    setSaving(true);
    setMessage("");
    try {
      const data = await updateAdminStoreGoogleCalendars({
        HqCalendar: {
          GoogleEmail: hqCalendar.GoogleEmail,
          CalendarId: hqCalendar.CalendarId,
          CalendarName: hqCalendar.CalendarName,
          IsEnabled: hqCalendar.IsEnabled,
        },
        Settings: settings.map((item) => ({
          LocationId: item.LocationId,
          GoogleEmail: item.GoogleEmail,
          CalendarId: item.CalendarId,
          CalendarName: item.CalendarName,
          IsEnabled: item.IsEnabled,
          IncludeInMergedView: item.IncludeInMergedView,
        })),
      });
      setHqCalendar(data.HqCalendar || emptyHqCalendar);
      setSettings(data.Settings || []);
      setDirty(false);
      setMessage(data.message || "Google 行事曆設定已儲存。");
      if (viewMode === "merged") {
        await loadMergedEvents(data.Settings || []);
      }
    } catch (error) {
      setMessage(error instanceof Error ? error.message : "Google 行事曆設定儲存失敗。");
      if (error instanceof AdminApiError && error.status === 401) navigate("/admin-login", { replace: true });
    } finally {
      setSaving(false);
    }
  }

  async function startOAuth(setting: AdminStoreGoogleCalendarSetting) {
    if (dirty) {
      setMessage("請先儲存 Google 帳號與 Calendar ID，再進行 Google 授權。");
      return;
    }
    if (!setting.GoogleEmail.trim() || !(setting.CalendarId || setting.GoogleEmail).trim()) {
      setMessage("請先填寫並儲存這間門市的 Gmail 與 Calendar ID，再進行授權。");
      return;
    }

    setAuthorizingLocationId(setting.LocationId);
    setMessage("");
    try {
      const data = await startAdminStoreGoogleCalendarOAuth(setting.LocationId);
      const popup = window.open(data.AuthorizationUrl, "_blank", "width=720,height=760");
      if (!popup) {
        setMessage("瀏覽器阻擋了 Google 授權視窗，請允許彈出視窗後再試一次。");
        return;
      }
      popup.opener = null;
      setMessage("Google 授權視窗已開啟。完成授權後請回到這裡按「重新載入」確認狀態。");
    } catch (error) {
      setMessage(error instanceof Error ? error.message : "Google 授權流程啟動失敗。");
      if (error instanceof AdminApiError && error.status === 401) navigate("/admin-login", { replace: true });
    } finally {
      setAuthorizingLocationId(null);
    }
  }

  async function startHeadquartersOAuth() {
    if (dirty) {
      setMessage("請先儲存總管理處 Google 帳號與 Calendar ID，再進行 Google 授權。");
      return;
    }
    if (!hqCalendar.GoogleEmail.trim() || !(hqCalendar.CalendarId || hqCalendar.GoogleEmail).trim()) {
      setMessage("請先填寫並儲存總管理處 Gmail 與 Calendar ID，再進行授權。");
      return;
    }

    setAuthorizingHeadquarters(true);
    setMessage("");
    try {
      const data = await startAdminHeadquartersGoogleCalendarOAuth();
      const popup = window.open(data.AuthorizationUrl, "_blank", "width=720,height=760");
      if (!popup) {
        setMessage("瀏覽器阻擋了 Google 授權視窗，請允許彈出視窗後再試一次。");
        return;
      }
      popup.opener = null;
      setMessage("總管理處 Google 授權視窗已開啟。完成授權後請回到這裡按「重新載入」確認狀態。");
    } catch (error) {
      setMessage(error instanceof Error ? error.message : "總管理處 Google 授權流程啟動失敗。");
      if (error instanceof AdminApiError && error.status === 401) navigate("/admin-login", { replace: true });
    } finally {
      setAuthorizingHeadquarters(false);
    }
  }

  async function loadHeadquartersEvents() {
    setEventsLoading(true);
    try {
      const timeMin = new Date(calendarViewDate.getFullYear(), calendarViewDate.getMonth() - 1, 1);
      const timeMax = new Date(calendarViewDate.getFullYear(), calendarViewDate.getMonth() + 2, 1);
      const data = await listAdminGoogleCalendarEvents({
        TargetType: "Headquarters",
        TimeMin: timeMin.toISOString(),
        TimeMax: timeMax.toISOString(),
      });
      setEvents(data.Events || []);
    } catch (error) {
      setMessage(error instanceof Error ? error.message : "Google 行事曆事件載入失敗。");
    } finally {
      setEventsLoading(false);
    }
  }

  async function loadMergedEvents(sourceSettings = settings) {
    const targets = sourceSettings.filter((setting) => setting.IsEnabled && setting.IncludeInMergedView && setting.OAuthStatus === "Connected" && Boolean((setting.CalendarId || setting.GoogleEmail).trim()));
    if (targets.length === 0) {
      setMergedEvents([]);
      return;
    }
    setMergedEventsLoading(true);
    try {
      const timeMin = new Date(calendarViewDate.getFullYear(), calendarViewDate.getMonth() - 1, 1);
      const timeMax = new Date(calendarViewDate.getFullYear(), calendarViewDate.getMonth() + 2, 1);
      const results = await Promise.all(
        targets.map(async (setting) => {
          try {
            const data = await listAdminGoogleCalendarEvents({
              TargetType: "Store",
              LocationId: setting.LocationId,
              TimeMin: timeMin.toISOString(),
              TimeMax: timeMax.toISOString(),
            });
            return (data.Events || []).map((event) => ({ ...event, SourceName: setting.LocationName }));
          } catch {
            return [];
          }
        }),
      );
      setMergedEvents(results.flat());
    } catch (error) {
      setMergedEvents([]);
      setMessage(error instanceof Error ? error.message : "全門市 Google 行事曆事件載入失敗。");
    } finally {
      setMergedEventsLoading(false);
    }
  }

  function updateEventForm(patch: Partial<GoogleCalendarEventForm>) {
    setEventForm((current) => (current ? { ...current, ...patch } : current));
  }

  function toggleAllDay(checked: boolean) {
    setEventForm((current) => (current ? toggleGoogleCalendarAllDayForm(current, checked) : current));
  }

  function eventPayloadFromForm(form: GoogleCalendarEventForm): AdminGoogleCalendarEventPayload {
    return {
      TargetType: "Headquarters",
      ...googleCalendarEventPayloadFromForm(form),
    };
  }

  async function saveEvent() {
    if (!eventForm) return;
    setEventSaving(true);
    setMessage("");
    try {
      if (eventForm.EventId) {
        const data = await updateAdminGoogleCalendarEvent(eventForm.EventId, eventPayloadFromForm(eventForm));
        setMessage(data.message || "Google 行事曆事件已更新。");
      } else {
        const data = await createAdminGoogleCalendarEvent(eventPayloadFromForm(eventForm));
        setMessage(data.message || "Google 行事曆事件已新增。");
      }
      setEventForm(null);
      await loadHeadquartersEvents();
    } catch (error) {
      setMessage(error instanceof Error ? error.message : "Google 行事曆事件儲存失敗。");
      if (error instanceof AdminApiError && error.status === 401) navigate("/admin-login", { replace: true });
    } finally {
      setEventSaving(false);
    }
  }

  async function removeEvent(event: AdminGoogleCalendarEvent) {
    const confirmed = await confirm({
      title: "刪除 Google 行事曆事件？",
      description: (
        <>
          你即將刪除 <strong className="font-bold text-[#203d32]">{event.Summary}</strong>。
          <br />
          此操作會同步刪除 Google 行事曆上的事件，無法復原。
        </>
      ),
      confirmText: "刪除",
      tone: "danger",
    });
    if (!confirmed) return;
    setEventSaving(true);
    setMessage("");
    try {
      const data = await deleteAdminGoogleCalendarEvent(event.EventId, { TargetType: "Headquarters" });
      setMessage(data.message || "Google 行事曆事件已刪除。");
      setEventForm(null);
      await loadHeadquartersEvents();
    } catch (error) {
      setMessage(error instanceof Error ? error.message : "Google 行事曆事件刪除失敗。");
      if (error instanceof AdminApiError && error.status === 401) navigate("/admin-login", { replace: true });
    } finally {
      setEventSaving(false);
    }
  }

  const mergeReadySettings = useMemo(
    () => settings.filter((setting) => setting.IsEnabled && Boolean((setting.CalendarId || setting.GoogleEmail).trim())),
    [settings],
  );
  const selectedMergeCount = mergeReadySettings.filter((setting) => setting.IncludeInMergedView).length;
  const selectedAuthorizedMergeCount = mergeReadySettings.filter((setting) => setting.IncludeInMergedView && setting.OAuthStatus === "Connected").length;
  const emptyFrameClass =
    "flex h-[calc(100svh-260px)] min-h-[420px] items-center justify-center rounded-md border border-dashed border-[#cfd8d2] bg-white px-6 text-center text-sm leading-6 text-[#7b877f] md:h-[calc(100vh-250px)]";
  const messageIsError = /失敗|錯誤|請先|阻擋|不正確|尚未|逾時|沒有|無法/.test(message);

  function moveCalendar(direction: -1 | 1) {
    setCalendarViewDate((current) => {
      const next = new Date(current);
      if (rangeMode === "WEEK") {
        next.setDate(current.getDate() + direction * 7);
      } else {
        next.setMonth(current.getMonth() + direction);
      }
      return next;
    });
  }

  function reloadCurrentView() {
    load();
    if (viewMode === "hq") {
      loadHeadquartersEvents();
    }
    if (viewMode === "merged") {
      loadMergedEvents();
    }
  }

  return (
    <AdminLayout sectionLabel="系統管理" title="門市日曆" user={currentUser} onLogout={handleLogout}>
      {message && (
        <div className={`fixed right-4 top-24 z-[80] max-w-[calc(100vw-32px)] rounded-md border bg-white px-4 py-3 text-sm font-bold shadow-[0_20px_50px_rgba(32,61,50,.16)] sm:max-w-md ${messageIsError ? "border-[#f2b8b5] text-[#8a1f1f]" : "border-[#cfe3d5] text-[#245438]"}`}>
          <div className="flex items-start gap-2">
            {messageIsError ? <AlertCircle className="mt-0.5 shrink-0" size={16} /> : <CheckCircle2 className="mt-0.5 shrink-0" size={16} />}
            <span>{message}</span>
            <button className="ml-2 inline-flex h-5 w-5 shrink-0 items-center justify-center rounded text-[#59665f] hover:bg-[#f0f5f2]" onClick={() => setMessage("")} type="button">
              <X size={13} />
            </button>
          </div>
        </div>
      )}

      <div className="mb-4 rounded-md border border-[#f0dfb8] bg-[#fdf6e3] px-4 py-3 text-sm leading-6 text-[#7a5b12]">
        <div className="flex items-center justify-between gap-3">
          <p className="font-bold">欄位說明：門市 Gmail 與 Calendar ID 用途不同。</p>
          <button
            aria-expanded={showFieldHelp}
            className="inline-flex h-8 w-8 shrink-0 items-center justify-center rounded-md border border-[#e7ca80] bg-white text-[#7a5b12] hover:bg-[#fff8e6]"
            onClick={() => setShowFieldHelp((current) => !current)}
            title={showFieldHelp ? "收合欄位說明" : "展開欄位說明"}
            type="button"
          >
            <HelpCircle size={16} />
          </button>
        </div>
        {showFieldHelp && (
          <div className="mt-2">
            <p>門市 Gmail 是門市登入 Google 的帳號；Calendar ID 是 Google 用來識別某一個行事曆的唯一代號。</p>
            <p className="mt-1">
              如果每間門市使用該 Gmail 帳號的「主行事曆」，Calendar ID 通常就等於門市 Gmail，例如 store01@gmail.com；這種情況直接按「帶入門市 Gmail」即可。
            </p>
            <p className="mt-1">
              如果門市是在同一個 Gmail 裡另外新增一份行事曆，例如「台北店預約表」，Calendar ID 可能會是 abc123def456@group.calendar.google.com，這時才需要手動填入該行事曆的 ID。
            </p>
            <p className="mt-1">
              若之後要在系統內新增、修改或刪除 Google 行事曆事件，每間門市 Gmail 都需要各自授權一次；系統只保存加密後的 OAuth token，不保存 Gmail 密碼。
            </p>
          </div>
        )}
      </div>

      <section className="mb-4 rounded-md border border-[#dfe6e1] bg-white p-4">
        <div className="mb-3 flex flex-col gap-3 lg:flex-row lg:items-center lg:justify-between">
          <div className="flex min-w-0 items-center gap-2">
            <span className="inline-flex h-9 w-9 items-center justify-center rounded-md bg-[#e9f4ec] text-[#245438]">
              <CalendarClock size={18} />
            </span>
            <div className="min-w-0">
              <h2 className="text-base font-extrabold text-[#203d32]">總管理處自己的主日曆</h2>
              <p className="text-xs font-medium text-[#7b877f]">
              這裡只會顯示總管理處帳號本身的主日曆；請在「門市設定」填各門市自己的 Gmail / Calendar ID。
              </p>
            </div>
          </div>
          <div className="grid w-full gap-2 lg:max-w-2xl lg:grid-cols-[120px_1fr_1fr]">
            <label className="flex h-10 items-center gap-2 text-sm font-bold text-[#59665f]">
              <input
                checked={hqCalendar.IsEnabled}
                className="h-4 w-4 accent-[#315746]"
                onChange={(event) => updateHqCalendar({ IsEnabled: event.target.checked })}
                type="checkbox"
              />
              啟用
            </label>
            <input
              className="h-10 rounded-md border border-[#cfd8d2] px-3 text-sm outline-none focus:border-[#315746] focus:ring-2 focus:ring-[#315746]/15"
              onChange={(event) => updateHqCalendar({ GoogleEmail: event.target.value, CalendarId: hqCalendar.CalendarId || event.target.value })}
              placeholder="總管理處 Gmail"
              value={hqCalendar.GoogleEmail}
            />
            <div className="flex gap-2">
              <input
                className="h-10 min-w-0 flex-1 rounded-md border border-[#cfd8d2] px-3 text-sm outline-none focus:border-[#315746] focus:ring-2 focus:ring-[#315746]/15"
                onChange={(event) => updateHqCalendar({ CalendarId: event.target.value })}
                placeholder="Calendar ID"
                value={hqCalendar.CalendarId}
              />
              <button
                className="h-10 shrink-0 rounded-md border border-[#cfd8d2] bg-white px-3 text-sm font-bold text-[#315746] hover:bg-[#f0f5f2]"
                onClick={() => updateHqCalendar({ CalendarId: hqCalendar.GoogleEmail })}
                type="button"
              >
                帶入總管理 Gmail
              </button>
            </div>
          </div>
        </div>
        <div className="rounded-md border border-[#dfe6e1] bg-[#f8faf8] p-3">
          <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
            <div className="min-w-0">
              {hqCalendar.OAuthStatus === "Connected" ? (
                <p className="flex items-center gap-1.5 text-sm font-extrabold text-[#245438]">
                  <CheckCircle2 size={16} />
                  總管理處已授權 Google 行事曆
                </p>
              ) : hqCalendar.OAuthStatus === "Error" ? (
                <p className="flex items-center gap-1.5 text-sm font-extrabold text-[#8a1f1f]">
                  <AlertCircle size={16} />
                  總管理處授權異常，請重新授權
                </p>
              ) : (
                <p className="flex items-center gap-1.5 text-sm font-extrabold text-[#7a5b12]">
                  <KeyRound size={16} />
                  總管理處尚未授權 Google 行事曆
                </p>
              )}
              <p className="mt-1 truncate text-xs font-medium text-[#7b877f]">
                {hqCalendar.OAuthGoogleEmail ? `授權帳號：${hqCalendar.OAuthGoogleEmail}` : "總管理處日曆若要新增、修改、刪除事件，也需要先完成 OAuth 授權。"}
              </p>
              {hqCalendar.OAuthLastErrorMessage && <p className="mt-1 text-xs font-medium text-[#8a1f1f]">{hqCalendar.OAuthLastErrorMessage}</p>}
            </div>
            <button
              className="inline-flex h-10 shrink-0 items-center justify-center gap-1.5 rounded-md bg-[#315746] px-3 text-sm font-bold text-white transition hover:bg-[#254536] disabled:cursor-not-allowed disabled:opacity-60"
              disabled={saving || authorizingHeadquarters}
              onClick={startHeadquartersOAuth}
              type="button"
            >
              <KeyRound size={15} />
              {authorizingHeadquarters ? "開啟中" : hqCalendar.OAuthStatus === "Connected" ? "重新授權" : "授權總管理 Gmail"}
            </button>
          </div>
        </div>
      </section>

      <div className="sticky top-[65px] z-10 mb-4 flex flex-wrap items-center justify-between gap-3 rounded-md border border-[#dfe6e1] bg-white p-3 shadow-[0_10px_30px_rgba(32,61,50,.08)] md:top-[73px]">
        <div className="inline-flex rounded-md border border-[#cfd8d2] p-1">
          <button
            className={`inline-flex h-9 items-center gap-1.5 rounded px-3 text-sm font-bold transition ${viewMode === "hq" ? "bg-[#315746] text-white" : "text-[#59665f] hover:bg-[#f0f5f2]"}`}
            onClick={() => setViewMode("hq")}
            type="button"
          >
            <CalendarClock size={16} />
            總管理處
          </button>
          <button
            className={`inline-flex h-9 items-center gap-1.5 rounded px-3 text-sm font-bold transition ${viewMode === "merged" ? "bg-[#315746] text-white" : "text-[#59665f] hover:bg-[#f0f5f2]"}`}
            onClick={() => setViewMode("merged")}
            type="button"
          >
            <Layers size={16} />
            全門市
          </button>
          <button
            className={`inline-flex h-9 items-center gap-1.5 rounded px-3 text-sm font-bold transition ${viewMode === "settings" ? "bg-[#315746] text-white" : "text-[#59665f] hover:bg-[#f0f5f2]"}`}
            onClick={() => setViewMode("settings")}
            type="button"
          >
            <Settings2 size={16} />
            門市設定
          </button>
        </div>
        <label className="inline-flex items-center gap-2 text-sm font-bold text-[#59665f]">
          檢視範圍
          <select
            className="h-9 rounded-md border border-[#cfd8d2] px-2 outline-none focus:border-[#315746] focus:ring-2 focus:ring-[#315746]/15"
            onChange={(event) => updateRangeMode(event.target.value as RangeMode)}
            value={rangeMode}
          >
            <option value="WEEK">週</option>
            <option value="MONTH">月</option>
            <option value="AGENDA">議程</option>
          </select>
        </label>
        <div className="flex items-center gap-2">
          <button
            className="inline-flex h-9 items-center gap-1.5 rounded-md border border-[#cfd8d2] bg-white px-3 text-sm font-bold text-[#315746] transition hover:bg-[#f0f5f2] disabled:cursor-not-allowed disabled:opacity-60"
            disabled={loading || saving}
            onClick={reloadCurrentView}
            type="button"
          >
            <RefreshCw size={15} />
            重新載入
          </button>
          <button
            className="inline-flex h-9 items-center gap-1.5 rounded-md bg-[#315746] px-3 text-sm font-bold text-white transition hover:bg-[#254536] disabled:cursor-not-allowed disabled:opacity-60"
            disabled={!dirty || saving}
            onClick={saveSettings}
            type="button"
          >
            <Save size={15} />
            {saving ? "儲存中" : "儲存設定"}
          </button>
        </div>
      </div>

      {loading ? (
        <div className="rounded-md border border-[#dfe6e1] bg-white px-4 py-8 text-center text-[#7b877f]">載入門市清單中...</div>
      ) : settings.length === 0 ? (
        <div className="rounded-md border border-[#dfe6e1] bg-white px-4 py-8 text-center text-[#7b877f]">目前沒有門市資料。</div>
      ) : viewMode === "hq" ? (
        <section className="rounded-md border border-[#dfe6e1] bg-white p-3">
          <div className="mb-3 flex flex-wrap items-center justify-between gap-2 px-1">
            <h2 className="text-base font-extrabold text-[#203d32]">總管理處日曆</h2>
            <div className="flex flex-wrap items-center gap-2">
              {hqCalendar.OAuthStatus === "Connected" && (
                <>
                  <button
                    className="inline-flex h-9 items-center gap-1.5 rounded-md bg-[#315746] px-3 text-sm font-bold text-white transition hover:bg-[#254536]"
                    onClick={() => setEventForm(defaultGoogleCalendarEventForm())}
                    type="button"
                  >
                    <Plus size={15} />
                    新增事件
                  </button>
                  <button
                    className="inline-flex h-9 items-center gap-1.5 rounded-md border border-[#cfd8d2] bg-white px-3 text-sm font-bold text-[#315746] transition hover:bg-[#f0f5f2]"
                    disabled={eventsLoading}
                    onClick={loadHeadquartersEvents}
                    type="button"
                  >
                    <RefreshCw size={15} />
                    更新事件
                  </button>
                </>
              )}
            </div>
          </div>
          {hqCalendar.OAuthStatus !== "Connected" && (
            <div className="mb-4 rounded-md border border-dashed border-[#cfd8d2] bg-[#f8faf8] px-4 py-3 text-sm font-medium text-[#7b877f]">
              要在系統內新增、編輯或刪除總管理處事件，請先完成上方「授權總管理 Gmail」。
            </div>
          )}
          {hqCalendar.OAuthStatus === "Connected" ? (
            <GoogleCalendarEventCalendarView
              events={events}
              loading={eventsLoading}
              rangeMode={rangeMode}
              viewDate={calendarViewDate}
              onEdit={(event) => setEventForm(googleCalendarEventToForm(event))}
              onMove={moveCalendar}
              onToday={() => setCalendarViewDate(new Date())}
            />
          ) : (
            <div className={emptyFrameClass}>請先完成總管理處 Google 授權，這裡會用系統行事曆畫面顯示事件。</div>
          )}
        </section>
      ) : viewMode === "settings" ? (
        <div className="grid gap-4 xl:grid-cols-2 2xl:grid-cols-3">
          {settings.map((setting) => {
            const calendarId = setting.CalendarId || setting.GoogleEmail;
            const trimmed = setting.IsEnabled ? calendarId.trim() : "";
            return (
              <div className="rounded-md border border-[#dfe6e1] bg-white p-4" key={setting.LocationId}>
                <div className="mb-3 flex items-center justify-between gap-2">
                  <div className="min-w-0">
                    <h2 className="truncate text-base font-extrabold text-[#203d32]">{setting.LocationName}</h2>
                    <p className="text-xs font-bold text-[#7b877f]">{setting.LocationIsActive ? "啟用門市" : "停用門市"}</p>
                  </div>
                  {trimmed && <span className="rounded-full bg-[#eef5f0] px-3 py-1 text-xs font-bold text-[#315746]">已設定 ID</span>}
                </div>
                <label className="mb-2 flex items-center gap-2 text-sm font-bold text-[#59665f]">
                  <input
                    checked={setting.IsEnabled}
                    className="h-4 w-4 accent-[#315746]"
                    onChange={(event) => updateSetting(setting.LocationId, { IsEnabled: event.target.checked, IncludeInMergedView: event.target.checked ? setting.IncludeInMergedView : false })}
                    type="checkbox"
                  />
                  啟用這間門市日曆
                </label>
                <label className="mb-3 flex items-center gap-2 text-sm font-bold text-[#59665f]">
                  <input
                    checked={setting.IncludeInMergedView}
                    className="h-4 w-4 accent-[#315746]"
                    disabled={!setting.IsEnabled}
                    onChange={(event) => updateSetting(setting.LocationId, { IncludeInMergedView: event.target.checked })}
                    type="checkbox"
                  />
                  納入全門市合併檢視
                </label>
                <input
                  className="mb-2 h-10 w-full rounded-md border border-[#cfd8d2] px-3 text-sm outline-none focus:border-[#315746] focus:ring-2 focus:ring-[#315746]/15"
                  onChange={(event) => updateSetting(setting.LocationId, { GoogleEmail: event.target.value, CalendarId: setting.CalendarId || event.target.value })}
                  placeholder="門市 Gmail，例如：store01@gmail.com"
                  value={setting.GoogleEmail}
                />
                <div className="mb-3 flex gap-2">
                  <input
                    className="h-10 min-w-0 flex-1 rounded-md border border-[#cfd8d2] px-3 text-sm outline-none focus:border-[#315746] focus:ring-2 focus:ring-[#315746]/15"
                    onChange={(event) => updateSetting(setting.LocationId, { CalendarId: event.target.value })}
                  placeholder="Calendar ID，通常可填同一個門市 Gmail"
                    value={setting.CalendarId}
                  />
                  <button
                    className="h-10 shrink-0 rounded-md border border-[#cfd8d2] bg-white px-3 text-sm font-bold text-[#315746] hover:bg-[#f0f5f2]"
                    onClick={() => updateSetting(setting.LocationId, { CalendarId: setting.GoogleEmail })}
                    type="button"
                  >
                    帶入門市 Gmail
                  </button>
                </div>
                <div className="mb-3 rounded-md border border-[#dfe6e1] bg-[#f8faf8] p-3">
                  <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
                    <div className="min-w-0">
                      {setting.OAuthStatus === "Connected" ? (
                        <p className="flex items-center gap-1.5 text-sm font-extrabold text-[#245438]">
                          <CheckCircle2 size={16} />
                          已授權 Google 行事曆
                        </p>
                      ) : setting.OAuthStatus === "Error" ? (
                        <p className="flex items-center gap-1.5 text-sm font-extrabold text-[#8a1f1f]">
                          <AlertCircle size={16} />
                          授權異常，請重新授權
                        </p>
                      ) : (
                        <p className="flex items-center gap-1.5 text-sm font-extrabold text-[#7a5b12]">
                          <KeyRound size={16} />
                          尚未授權 Google 行事曆
                        </p>
                      )}
                      <p className="mt-1 truncate text-xs font-medium text-[#7b877f]">
                        {setting.OAuthGoogleEmail ? `授權帳號：${setting.OAuthGoogleEmail}` : "新增/修改/刪除事件前，需先用這間門市 Gmail 完成授權。"}
                      </p>
                      {setting.OAuthLastErrorMessage && <p className="mt-1 text-xs font-medium text-[#8a1f1f]">{setting.OAuthLastErrorMessage}</p>}
                    </div>
                    <button
                      className="inline-flex h-10 shrink-0 items-center justify-center gap-1.5 rounded-md bg-[#315746] px-3 text-sm font-bold text-white transition hover:bg-[#254536] disabled:cursor-not-allowed disabled:opacity-60"
                      disabled={saving || authorizingLocationId === setting.LocationId}
                      onClick={() => startOAuth(setting)}
                      type="button"
                    >
                      <KeyRound size={15} />
                      {authorizingLocationId === setting.LocationId ? "開啟中" : setting.OAuthStatus === "Connected" ? "重新授權" : "授權門市 Gmail"}
                    </button>
                  </div>
                </div>
                <div className={`rounded-md border px-4 py-5 text-sm leading-6 ${trimmed && setting.OAuthStatus === "Connected" ? "border-[#cfe3d5] bg-[#f4faf5] text-[#245438]" : "border-[#f0dfb8] bg-[#fdf6e3] text-[#7a5b12]"}`}>
                  <p className="font-extrabold">{trimmed && setting.OAuthStatus === "Connected" ? "可用系統行事曆檢視" : "尚未完成系統檢視條件"}</p>
                  <p className="mt-1">
                    {trimmed && setting.OAuthStatus === "Connected"
                      ? "這間門市已可透過 Google API 載入事件；若要一起看，請勾選「納入全門市合併檢視」。"
                      : "請先填寫門市 Gmail / Calendar ID，並完成這間門市 Google 授權。"}
                  </p>
                </div>
              </div>
            );
          })}
        </div>
      ) : (
        <div className="rounded-md border border-[#dfe6e1] bg-white p-4">
          <div className="mb-3 flex flex-col gap-3 lg:flex-row lg:items-start lg:justify-between">
            <div>
              <h2 className="text-base font-extrabold text-[#203d32]">全門市合併檢視</h2>
              <p className="mt-1 text-xs font-bold text-[#59665f]">目前勾選 {selectedMergeCount} / {mergeReadySettings.length} 間，已授權可載入 {selectedAuthorizedMergeCount} 間</p>
            </div>
            <div className="flex flex-wrap items-center gap-2">
              <button
                className="h-9 rounded-md border border-[#cfd8d2] bg-white px-3 text-sm font-bold text-[#315746] hover:bg-[#f0f5f2]"
                onClick={() => {
                  setSettings((current) => current.map((item) => ({ ...item, IncludeInMergedView: item.IsEnabled && Boolean((item.CalendarId || item.GoogleEmail).trim()) })));
                  setDirty(true);
                }}
                type="button"
              >
                全選可顯示門市
              </button>
              <button
                className="h-9 rounded-md border border-[#cfd8d2] bg-white px-3 text-sm font-bold text-[#59665f] hover:bg-[#f0f5f2]"
                onClick={() => {
                  setSettings((current) => current.map((item) => ({ ...item, IncludeInMergedView: false })));
                  setDirty(true);
                }}
                type="button"
              >
                全不選
              </button>
            </div>
          </div>
          <div className="mb-4 rounded-md border border-[#dfe6e1] bg-[#f8faf8] p-3">
            <div className="grid gap-2 sm:grid-cols-2 lg:grid-cols-3 2xl:grid-cols-4">
              {settings.map((setting) => {
                const canMerge = setting.IsEnabled && Boolean((setting.CalendarId || setting.GoogleEmail).trim());
                return (
                  <label className={`flex min-h-12 items-center gap-2 rounded-md border px-3 py-2 text-sm font-bold ${canMerge ? "border-[#dfe6e1] bg-white text-[#203d32]" : "border-[#edf0ee] bg-[#f5f7f5] text-[#9aa6a0]"}`} key={`merge-${setting.LocationId}`}>
                    <input
                      checked={setting.IncludeInMergedView && canMerge}
                      className="h-4 w-4 shrink-0 accent-[#315746]"
                      disabled={!canMerge}
                      onChange={(event) => updateSetting(setting.LocationId, { IncludeInMergedView: event.target.checked })}
                      type="checkbox"
                    />
                    <span className="min-w-0 flex-1 truncate">{setting.LocationName}</span>
                    {!canMerge && <span className="shrink-0 text-[11px] font-bold">未設定</span>}
                  </label>
                );
              })}
            </div>
            {dirty && <p className="mt-3 text-xs font-bold text-[#7a5b12]">勾選變更後請按上方「儲存設定」，全門市檢視才會永久保存。</p>}
          </div>
          {selectedAuthorizedMergeCount > 0 ? (
            <GoogleCalendarEventCalendarView
              events={mergedEvents}
              loading={mergedEventsLoading}
              rangeMode={rangeMode}
              viewDate={calendarViewDate}
              emptyMessage="目前範圍內沒有門市事件。"
              onMove={moveCalendar}
              onToday={() => setCalendarViewDate(new Date())}
            />
          ) : (
            <div className={emptyFrameClass}>
              請先在「門市設定」勾選要納入的門市，並完成該門市 Google 授權，這裡會用系統行事曆畫面合併顯示事件。
            </div>
          )}
        </div>
      )}
      {eventForm && (
        <GoogleCalendarEventFormModal
          form={eventForm}
          saving={eventSaving}
          onCancel={() => setEventForm(null)}
          onChange={updateEventForm}
          onDelete={
            eventForm.EventId
              ? () => {
                  const target = events.find((item) => item.EventId === eventForm.EventId);
                  if (target) removeEvent(target);
                }
              : undefined
          }
          onMessage={setMessage}
          onSave={saveEvent}
          onToggleAllDay={toggleAllDay}
        />
      )}      <ConfirmDialog />
    </AdminLayout>
  );
}


